// React-free auth state store plus the session actions (login, logout, retry) that drive it.

import { ensureClientId } from 'services/auth/client-registration';
import {
  OAUTH_CODE_VERIFIER,
  OAUTH_RETURN_TO,
  OAUTH_SCOPE,
  OAUTH_STATE,
  oauthAuthorizeUrl,
  redirectUri,
} from 'services/auth/constants';
import { createChallenge, createState, createVerifier } from 'services/auth/pkce';
import { logout as revokeAndClear } from 'services/auth/logout';
import { authEvents } from 'services/auth/session';
import { OAuthError } from 'services/auth/token-endpoint';
import { getMemoryToken, getValidAccessToken, releaseRefreshLatch } from 'services/auth/token-store';

export type AuthStatus = 'loading' | 'authenticated' | 'degraded' | 'anonymous';
export type AuthState = { status: AuthStatus; token: string | null };
type Listener = (state: AuthState) => void;

let state: AuthState = { status: 'loading', token: null };
const listeners = new Set<Listener>();

const update = (status: AuthStatus): void => {
  state = { status, token: getMemoryToken() };
  Array.from(listeners).forEach((listener) => listener(state));
};

// The snapshot token is re-read on every call, so a clear that emits no event can never leave a stale token behind.
export const getState = (): AuthState => {
  const token = getMemoryToken();
  if (token !== state.token) state = { ...state, token };
  return state;
};

export const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export const setStatus = (status: AuthStatus): void => update(status);

authEvents.on('token-changed', () => update('authenticated'));
authEvents.on('unauthenticated', () => update('anonymous'));
authEvents.on('degraded', () => update('degraded'));

export const authStore = { getState, subscribe };

// One refresh attempt mapped to a status. Only invalid_grant / invalid_client end the session; everything else keeps tokens.
export const refreshSession = async (): Promise<AuthStatus> => {
  try {
    await getValidAccessToken();
    update('authenticated');
    return 'authenticated';
  } catch (error) {
    const endsSession = error instanceof OAuthError && (error.code === 'invalid_grant' || error.code === 'invalid_client');
    const status: AuthStatus = endsSession ? 'anonymous' : 'degraded';
    update(status);
    return status;
  }
};

// Releases the degraded latch, then runs exactly one refresh.
export const retrySession = (): Promise<AuthStatus> => {
  releaseRefreshLatch();
  return refreshSession();
};

// Revokes server-side (best effort), clears local state and navigates to /sign-in.
export const logout = (): Promise<void> => revokeAndClear();

export type LoginOptions = { returnTo?: string; oauthToken?: string };

const isSafeReturnTo = (value: string): boolean => value.startsWith('/') && !value.startsWith('//');

// Starts the PKCE authorize redirect. Registration errors (OAuthClientRegistrationError) propagate to the caller.
export const login = async ({ returnTo, oauthToken }: LoginOptions = {}): Promise<void> => {
  const clientId = await ensureClientId();
  const verifier = createVerifier();
  const oauthState = createState();
  const challenge = await createChallenge(verifier);

  sessionStorage.setItem(OAUTH_CODE_VERIFIER, verifier);
  sessionStorage.setItem(OAUTH_STATE, oauthState);
  if (returnTo !== undefined && isSafeReturnTo(returnTo)) sessionStorage.setItem(OAUTH_RETURN_TO, returnTo);
  else sessionStorage.removeItem(OAUTH_RETURN_TO);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: redirectUri(),
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state: oauthState,
    scope: OAUTH_SCOPE,
  });
  if (oauthToken) params.append('oauth_token', oauthToken);

  window.location.assign(`${oauthAuthorizeUrl()}?${params.toString()}`);
};
